import { iLoginService } from "../../types";
import { Signal, system } from "portalframework";
import * as ko from "knockout";
import * as $ from "jquery";
import * as _ from "lodash";
import * as toastr from "toastr";
import { portalHttpService } from "../_common/httpService";
import { jwtDecode } from "jwt-decode";
import { KyoceraToken } from "./KyoceraToken.interface";
import { profileService, RoutesService, httpErrorHandler, LastKnownPath } from "services";
import { BaseService } from "../_common/_baseService";
import { systemSpinner } from "app/global-spinner";
import { USER_LANGUAGE_KEY } from "app/app-constants";

const config = require("settings");

const TFA_REMEMBER_KEY = "token_2fa_remembered";

class MetrixLoginService extends BaseService implements iLoginService {
  //
  logoutStarted = ko.observable(false);

  access_token: KnockoutObservable<string>;
  parsedToken: KnockoutComputed<KyoceraToken | null>;
  FirstName: KnockoutComputed<string | null>;
  LastName: KnockoutComputed<string | null>;
  PostingGroup: KnockoutComputed<string | null>;

  constructor() {
    super("identity");
    this.access_token = ko.observable();
    this.parsedToken = ko.computed(() => {
      const token = this.access_token();
      if (token) {
        let parsedToken: KyoceraToken;
        try {
          parsedToken = jwtDecode<KyoceraToken>(token);
        } catch (error) {
          this.clearLocalStorage();
          window.location.reload();
          return null;
        }

        var expiry_dt = GetExpiryDate(parsedToken.exp);
        if (config.storeAuthForLongRunningSessions) {
          const places = parsedToken.places.split(",");
          const placesNames = parsedToken.places_names.split(",");

          localStorage.setItem(config.tokenKey, token);
          localStorage.setItem("PlaceForPortalAccess", places.length > 0 ? places[0] : null);
          localStorage.setItem("PlaceForPortalAccessName", placesNames.length > 0 ? placesNames[0] : null);
          localStorage.setItem("FirstName", parsedToken.first_name);
          localStorage.setItem("LastName", parsedToken.last_name);
          localStorage.setItem("PersonId", parsedToken.person_id);
          localStorage.setItem("PostingGroup", parsedToken.posting_group);
          localStorage.setItem("SalesOrg", parsedToken.sales_org);
          localStorage.setItem("AccessRoles", parsedToken.user_roles);
          system.signals[Signal.AccessRightsUpdated].dispatch();
          // Use dynamic import to avoid circular dependency
          import("app/labels").then((labelsModule) => labelsModule.default.update());
          system.signals[Signal.PasswordWillExpire].dispatch(
            parsedToken.password_expire_date,
            parsedToken.password_expire_warn_days,
          );

          localStorage.setItem(config.tokenKey + "_username", parsedToken.email_address);
          localStorage.setItem(config.tokenKey + "_expires", expiry_dt.toString());
        }

        return parsedToken;
      }
      return null;
    });

    this.FirstName = ko.computed(() => {
      const token = this.parsedToken();
      return token ? token.first_name : null;
    });

    this.LastName = ko.computed(() => {
      const token = this.parsedToken();
      return token ? token.last_name : null;
    });

    this.PostingGroup = ko.computed(() => {
      const token = this.parsedToken();
      return token ? token.posting_group : null;
    });

    this.access_token.subscribe((newval: string) => {
      const headers: any = {};
      headers.Authorization = "Bearer " + newval;
      headers.PortalType = system.PortalType();
      $.ajaxSetup({
        headers: headers,
        xhrFields: { withCredentials: true },
        crossDomain: true,
        contentType: "application/json",
      });
    });

    $.ajaxSetup({
      crossDomain: true,
      contentType: "application/json",
    });

    $.ajaxSettings.error = (jqXHR, textStatus, errorThrown) => {
      if (jqXHR.status == 401) {
        const currentLocation = window.location.pathname;
        const currentMoment = new Date().toISOString();

        const data: LastKnownPath = {
          path: currentLocation,
          personId: this.parsedToken().person_id,
          created: currentMoment,
        };

        localStorage.setItem(RoutesService.LAST_KNOWN_PATH, JSON.stringify(data));

        const message = jqXHR?.responseJSON?.Message || ko.toJSON(jqXHR.responseJSON);
        // Use dynamic import to avoid circular dependency
        import("app/labels").then((labelsModule) => {
          toastr.error(labelsModule.default.descr(message));
        });
        this.performLogout(true);
      }
    };

    if (config.storeAuthForLongRunningSessions && localStorage.getItem(config.tokenKey)) {
      var expiryDate = new Date(localStorage.getItem(config.tokenKey + "_expires"));
      if (expiryDate > new Date()) {
        system.signedIn(true);
        this.access_token(localStorage.getItem(config.tokenKey));
      } else {
        system.signedIn(false);
        this.access_token(null);
        this.clearLocalStorage();
        system.navigate("/login");
      }
    } else {
      let arr = [
        "/logout",
        "/register",
        "/confirm",
        "/verify",
        "/data-privacy",
        "/legal-notices-terms",
        "/cookies",
        "/login/reset-verify",
        "/attachment",
        "/api",
        "/meters-login",
        "/meters-submission",
        "/meters-historical",
      ];
      if (
        !_.find(arr, (item) => {
          return system.history.location.pathname === item || system.history.location.pathname.startsWith(item + "/");
        })
      ) {
        const urlParams = new URLSearchParams(window.location.search);
        const locale = urlParams.get("locale");
        if (locale) {
          system.navigate(`/login?locale=${locale}`);
        } else {
          system.navigate(`/login`);
        }
      }
    }
  }

  performLogin = async (username: string, password: string, twoStepAuth: string, rememberTwoFactor?: boolean) => {
    const deviceKey = localStorage.getItem(TFA_REMEMBER_KEY);
    const loginData = {
      username: username,
      password: password,
      twoFactorCode: twoStepAuth,
      rememberTwoFactor: rememberTwoFactor,
      twoFactorDeviceKey: deviceKey ? deviceKey : null,
    };

    const data = await portalHttpService.post<{
      token: string;
      accessRights: any[];
      userLocaleCode: string;
      twoFactorDeviceKey: string;
    }>(`${system.apiURL()}api/identity/login`, ko.toJSON(loginData), false);
    localStorage.setItem(
      "AccessRights",
      JSON.stringify({
        AccessRights: data.accessRights,
      }),
    );
    localStorage.setItem("UserLocaleCode", data.userLocaleCode);

    // Store 2FA devicekey
    if (data.twoFactorDeviceKey) {
      localStorage.setItem(TFA_REMEMBER_KEY, data.twoFactorDeviceKey);
    }

    system.signedIn(true);
    this.access_token(data.token);

    const tfaSettings = await profileService.getTFASettings();
    RoutesService.TwoFactorSettings(tfaSettings);

    system.signals[Signal.AccessRightsUpdated].dispatch();
    system.signals[Signal.userLoggedOn].dispatch();
  };

  performLogout = async (metrixAlreadyLoggedOut: boolean): Promise<void> => {
    systemSpinner.spin();
    if (this.logoutStarted()) {
      return;
    }

    this.logoutStarted(true);

    if (!metrixAlreadyLoggedOut) {
      await portalHttpService.post<any>(system.apiURL() + "api/identity/Logout");
    }

    if (system.signedIn()) {
      system.signedIn(false);
      this.access_token(null);

      if (localStorage.getItem("LogoutDueInactivity")) {
        this.clearLocalStorage();
        localStorage.setItem("LogoutDueInactivity", "true");
      } else {
        this.clearLocalStorage(metrixAlreadyLoggedOut);
      }
      if (config.storeAuthForLongRunningSessions) {
        localStorage.removeItem(config.tokenKey);
        localStorage.removeItem(config.tokenKey + "_username");
        localStorage.removeItem(config.tokenKey + "_expires");
      }
    } else {
      this.clearLocalStorage(metrixAlreadyLoggedOut);
    }

    window.location.href = "/logout"; //!! don't use navigation. We need to clear any pending requests that will trigger again logout
  };

  resetpassword = async (email: string): Promise<{}> => {
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);
    return await portalHttpService.post<any>(
      system.apiURL() + "api/Account/resetpassword",
      ko.toJSON({ email: email, locale: locale }),
    );
  };

  verify_password_reset = async (email: string, key: string): Promise<{}> => {
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);
    return await portalHttpService.post<any>(
      system.apiURL() + "api/Account/verifyresetpassword",
      ko.toJSON({
        email: email,
        key: key,
        locale: locale,
      }),
    );
  };

  reset2FA = async (person_id: string): Promise<void> => {
    const payload = ko.toJSON({ person_id: person_id });
    await portalHttpService.post(`${this.baseApiUrl()}/Reset2FA`, payload);
  };

  getTwoFactorQRCode = async (): Promise<object> => {
    const data: any = await portalHttpService.get(`${this.baseApiUrl()}/GetTwoFactorQRCode`);
    return data;
  };

  addTwoFactorCode = async (received_code: string, secret_key: string): Promise<void> => {
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);
    const payload = ko.toJSON({ secret_key, received_code, locale });
    await portalHttpService.post(`${this.baseApiUrl()}/AddTwoFactorCode`, payload);
  };

  clearLocalStorage = (metrixAlreadyLoggedOut = false): void => {
    const userLanguage = localStorage.getItem(USER_LANGUAGE_KEY);
    const deviceKey = localStorage.getItem(TFA_REMEMBER_KEY);

    let lastKnownPath: string | undefined = undefined;
    if (metrixAlreadyLoggedOut) {
      lastKnownPath = localStorage.getItem(RoutesService.LAST_KNOWN_PATH);
    }

    localStorage.clear();
    if (userLanguage) {
      localStorage.setItem(USER_LANGUAGE_KEY, userLanguage);
    }

    if (deviceKey) {
      localStorage.setItem(TFA_REMEMBER_KEY, deviceKey);
    }

    if (lastKnownPath) {
      localStorage.setItem(RoutesService.LAST_KNOWN_PATH, lastKnownPath);
    }
  };
}

function GetExpiryDate(seconds) {
  return new Date(new Date().getTime() + seconds * 1000);
}

export const metrixLoginService = new MetrixLoginService();
